import { useState } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { trpc } from '@/lib/trpc'
import type { HomeworkTask } from '../data/types'

interface SystemDesignSubmissionDialogProps {
  task: HomeworkTask
  open: boolean
  onOpenChange: (open: boolean) => void
}

/**
 * Client submission form for system-design assignments: persists the
 * structured artifact and freezes the versioned submission snapshot.
 */
export function SystemDesignSubmissionDialog({
  task,
  open,
  onOpenChange,
}: SystemDesignSubmissionDialogProps) {
  const utils = trpc.useUtils()
  const mutation = trpc.dashboard.plan.submitForReview.useMutation()
  const [requirements, setRequirements] = useState('')
  const [diagramRef, setDiagramRef] = useState('')
  const [notes, setNotes] = useState('')
  const [minutes, setMinutes] = useState(45)
  const [error, setError] = useState<string | null>(null)

  const canSubmit =
    requirements.trim() !== '' &&
    task.assignmentVersion !== undefined &&
    !mutation.isPending

  const submit = async () => {
    if (!canSubmit) return
    setError(null)
    try {
      await mutation.mutateAsync({
        assignmentId: task.id,
        assignmentVersion: task.assignmentVersion!,
        commandId: crypto.randomUUID(),
        sourceId: crypto.randomUUID(),
        kind: 'system_design',
        durationSeconds: Math.max(0, Math.round(minutes)) * 60,
        artifact: {
          prompt: task.title,
          requirements,
          diagramRef: diagramRef.trim() === '' ? null : diagramRef.trim(),
          notes: notes.trim() === '' ? null : notes.trim(),
        },
        problemSessionId: null,
        interviewSessionId: null,
      })
      await Promise.all([
        utils.dashboard.plan.list.invalidate(),
        utils.dashboard.feedback.list.invalidate(),
      ])
      onOpenChange(false)
    } catch {
      setError('Could not submit for review. Please try again.')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>Submit for coach review</DialogTitle>
          <DialogDescription>
            {task.title} — your design is frozen exactly as submitted and queued
            for your coach.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sd-requirements">Requirements and approach</Label>
            <Textarea
              id="sd-requirements"
              value={requirements}
              onChange={(event) => setRequirements(event.target.value)}
              placeholder="Functional and non-functional requirements, capacity estimates, and your final design."
              rows={6}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sd-diagram">Diagram link (optional)</Label>
            <Input
              id="sd-diagram"
              value={diagramRef}
              onChange={(event) => setDiagramRef(event.target.value)}
              placeholder="https://excalidraw.com/…"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sd-notes">Notes for your coach (optional)</Label>
            <Textarea
              id="sd-notes"
              value={notes}
              onChange={(event) => setNotes(event.target.value)}
              rows={3}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="sd-minutes">Time spent (minutes)</Label>
            <Input
              id="sd-minutes"
              type="number"
              min={0}
              value={minutes}
              onChange={(event) => setMinutes(Number(event.target.value))}
            />
          </div>
          {error && <p className="text-sm text-destructive">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button onClick={submit} disabled={!canSubmit}>
              {mutation.isPending ? 'Submitting…' : 'Submit for review'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
